// BonBoZ: tính đoạn màu và tóm tắt cho thanh 24 giờ (PushTimeline.vue).
// Hàm thuần, không đụng Vue, để test riêng.
//
// Server trả mỗi tin là `[phút trong ngày, kết quả]` (xem
// `Bonboz::PushReport::Timeline`). Ở đây:
//   - mỗi tin tô màu từ lúc gửi tới tin kế tiếp; vắng tin quá GAP_MINUTES thì
//     phần sau đó là xám, vì không có tin nào để biết điện thoại nhận hay không;
//   - `off` (nhân viên tắt trong cài đặt) và `pending` (chưa có kết quả) không
//     lên thanh: không phải lỗi đường truyền, cũng chưa phải kết quả.

export const DAY_MINUTES = 24 * 60;
export const GAP_MINUTES = 30;

// Kết quả -> màu trên thanh. Không có trong bảng thì không lên thanh.
export const COLOR_OF = {
  ok: 'ok',
  resent: 'resent',
  rejected: 'lost',
  no_ack: 'lost',
  not_sent: 'lost',
};

export const hhmm = minute => {
  const m = Math.min(Math.max(Math.round(minute), 0), DAY_MINUTES);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

export const barEvents = events =>
  (events || [])
    .filter(([, outcome]) => COLOR_OF[outcome])
    .map(([minute, outcome]) => ({ minute, outcome, color: COLOR_OF[outcome] }))
    .sort((a, b) => a.minute - b.minute);

const merge = segments =>
  segments.reduce((out, segment) => {
    const last = out[out.length - 1];
    if (last && last.color === segment.color && last.end === segment.start) {
      last.end = segment.end;
      last.events.push(...segment.events);
    } else {
      out.push({ ...segment, events: [...segment.events] });
    }
    return out;
  }, []);

// Đoạn màu của một người trong một ngày: [{ start, end, color, events }],
// `color` là ok | resent | lost | empty, phủ kín 0 → 24:00.
export const segmentsOf = events => {
  const list = barEvents(events);
  const segments = [];
  let cursor = 0;
  list.forEach((event, index) => {
    // Hai tin cùng một phút: tin sau bắt đầu ngay sau đoạn của tin trước.
    const start = Math.max(event.minute, cursor);
    if (start >= DAY_MINUTES) return;
    if (start > cursor) {
      segments.push({ start: cursor, end: start, color: 'empty', events: [] });
    }
    const next = list[index + 1];
    const reach =
      next && next.minute - event.minute <= GAP_MINUTES
        ? next.minute
        : event.minute + GAP_MINUTES;
    const end = Math.min(Math.max(reach, start + 1), DAY_MINUTES);
    segments.push({ start, end, color: event.color, events: [event] });
    cursor = end;
  });
  if (cursor < DAY_MINUTES) {
    segments.push({
      start: cursor,
      end: DAY_MINUTES,
      color: 'empty',
      events: [],
    });
  }
  return merge(segments);
};

// Mốc giờ ở chỗ đổi màu. Hai hàng để chữ không đè nhau; gần quá cả hai hàng
// thì bỏ mốc đó (vẫn xem được khi bấm vào đoạn).
export const changeLabels = (segments, minGap = 75) => {
  const lastAt = [-Infinity, -Infinity];
  return segments.slice(1).reduce((labels, segment) => {
    const row = [0, 1].find(r => segment.start - lastAt[r] >= minGap);
    if (row === undefined) return labels;
    lastAt[row] = segment.start;
    labels.push({ minute: segment.start, row });
    return labels;
  }, []);
};

// Cụm tin liền nhau (cách nhau không quá GAP_MINUTES) thoả `pick`, để ghi khung giờ.
export const clusters = (events, pick) =>
  barEvents(events)
    .filter(pick)
    .reduce((out, event) => {
      const last = out[out.length - 1];
      if (last && event.minute - last.end <= GAP_MINUTES) {
        last.end = event.minute;
        last.count += 1;
      } else {
        out.push({ start: event.minute, end: event.minute, count: 1 });
      }
      return out;
    }, []);

export const formatRanges = list =>
  list
    .map(({ start, end }) =>
      start === end ? hhmm(start) : `${hhmm(start)}–${hhmm(end)}`
    )
    .join(', ');

// Tóm tắt một người trong một ngày.
export const summarize = events => {
  const all = events || [];
  const count = outcome => all.filter(([, o]) => o === outcome).length;
  const resent = count('resent');
  const lost = count('rejected') + count('no_ack') + count('not_sent');
  return {
    total: barEvents(all).length,
    ok: count('ok'),
    resent,
    lost,
    missed: resent + lost,
    off: count('off'),
    missedRanges: formatRanges(clusters(all, e => e.color !== 'ok')),
    resentRanges: formatRanges(clusters(all, e => e.color === 'resent')),
    lostRanges: formatRanges(clusters(all, e => e.color === 'lost')),
  };
};

// Máy chưa khai loại (đăng ký cũ, máy đã xoá) tính là điện thoại, như các số
// liệu khác của trang.
export const deviceInScope = (device, scope) => {
  if (scope === 'all') return true;
  return scope === 'desktop'
    ? device.type === 'desktop'
    : device.type !== 'desktop';
};

// Số cho sơ đồ đường đi Hệ thống → Apple/Google → Thiết bị, cộng mọi ngày,
// mọi người, mọi máy trong phạm vi. Cộng dồn khớp nhau:
//   total    = notSent + sent           (lượt cần gửi tới máy)
//   sent     = rejected + pending + accepted   (lượt đã thật sự gửi đi)
//   accepted = noAck + reached,  reached = ok + resent
// Luồng trên sơ đồ bắt đầu từ `sent`. `notSent` (máy hỏng), `off` (tắt trong
// cài đặt) và `noDevice` (chưa có máy) là phần KHÔNG gửi đi, ghi riêng.
export const pathTotals = (timeline, scope) => {
  const totals = {
    total: 0,
    sent: 0,
    notSent: 0,
    rejected: 0,
    pending: 0,
    accepted: 0,
    noAck: 0,
    reached: 0,
    ok: 0,
    resent: 0,
    off: 0,
    noDevice: 0,
  };
  const add = (key, count = 1) => {
    totals[key] += count;
  };
  const OUTCOME_TO = {
    ok: ['ok', 'reached', 'accepted', 'sent'],
    resent: ['resent', 'reached', 'accepted', 'sent'],
    no_ack: ['noAck', 'accepted', 'sent'],
    rejected: ['rejected', 'sent'],
    not_sent: ['notSent'],
    pending: ['pending', 'sent'],
  };
  (timeline?.agents || []).forEach(agent => {
    Object.values(agent.off || {}).forEach(count => add('off', count));
    Object.values(agent.no_device || {}).forEach(count =>
      add('noDevice', count)
    );
    (agent.devices || [])
      .filter(device => deviceInScope(device, scope))
      .forEach(device => {
        Object.values(device.days || {})
          .flat()
          .forEach(([, outcome]) => {
            const keys = OUTCOME_TO[outcome];
            if (!keys) return;
            add('total');
            keys.forEach(key => add(key));
          });
      });
  });
  return totals;
};
